import posthog from 'posthog-js';

import { env } from './env';
import { initFeatureFlagOverrides } from './featureFlagOverrides';

/**
 * Initialize PostHog for feature flags and A/B testing.
 * SDK init is skipped when `VITE_PUBLIC_POSTHOG_KEY` is not set; the
 * `window.posthogCapture` GTM bridge is still installed as a no-op.
 *
 * Autocapture, pageviews and session recording are intentionally disabled —
 * product analytics and page tracking are handled by GTM, error tracking
 * by Sentry. PostHog is only used here for feature flags and experiments.
 *
 * Persistence is `localStorage` only (no cookie). This avoids writing a
 * tracking cookie that would conflict with GTM Consent Mode v2 defaults
 * (see `gtm.ts`).
 *
 * On non-production environments, AQA feature-flag overrides from the
 * `bl_feature_flag_overrides` cookie are applied right after init — see
 * `featureFlagOverrides.ts`.
 */
export const initPostHog = (): void => {
  if (env.posthog.enabled) {
    posthog.init(env.posthog.key, {
      api_host: '/ingest',
      ui_host: env.posthog.host,
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      disable_session_recording: true,
      persistence: 'localStorage'
    });

    initFeatureFlagOverrides();
  }

  exposePostHogCapture();
};

/**
 * Exposes `window.posthogCapture` so GTM can forward events through the
 * identified PostHog instance. Capture-only — never the full SDK. Always
 * assigned (unlike {@link exposePostHog}); a no-op when PostHog is disabled.
 */
export const exposePostHogCapture = (): void => {
  window.posthogCapture = (event, properties) => {
    if (!env.posthog.enabled) {
      return;
    }

    posthog.capture(event, properties);
  };
};

/**
 * Exposes the PostHog client on `window` so feature flags can be inspected
 * and evaluated from the browser console. Called on non-production
 * environments only (gated in main.tsx).
 */
export const exposePostHog = (): void => {
  window.posthog = posthog;
  console.info(
    'window.posthog exposed for flag debugging:\n' +
      '  posthog.featureFlags.getFlagsWithDetails()  — all flags + evaluation reasons\n' +
      "  posthog.getFeatureFlag('flag-key')          — value of one flag (any key)\n" +
      "  posthog.isFeatureEnabled('flag-key')        — boolean check for one flag\n" +
      "  posthog.getFeatureFlagResult('flag-key')    — value + payload + evaluation reason\n" +
      '  posthog.reloadFeatureFlags()                — re-fetch fresh evaluations\n' +
      '  posthog.debug()                             — log /flags payloads'
  );
};

export { posthog };
